import { useId } from 'react';
import { cn } from '@/lib/utils';

interface SparklineProps {
  /** Series of values, oldest first. */
  data: number[];
  width?: number;
  height?: number;
  /** Tailwind text-color class used for the stroke and gradient fill. */
  colorClass?: string;
  /** Fixed value range. Defaults to 0-100 (score domain). */
  min?: number;
  max?: number;
  /** Draw a dashed horizontal reference line at this value (e.g. 50 = neutral). */
  baseline?: number;
  strokeWidth?: number;
  className?: string;
}

/**
 * A minimalist, dependency-free SVG sparkline. Renders a smoothed line with a
 * soft gradient area fill and a highlighted end point, matching the hand-rolled
 * SVG style used elsewhere (e.g. ScoreDial).
 */
export function Sparkline({
  data,
  width = 120,
  height = 36,
  colorClass = 'text-primary',
  min = 0,
  max = 100,
  baseline,
  strokeWidth = 2,
  className,
}: SparklineProps) {
  const gradientId = useId();

  if (data.length < 2) return null;

  const range = max - min || 1;
  const pad = strokeWidth; // keep stroke inside the viewBox
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;

  const points = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * innerW;
    const clamped = Math.max(min, Math.min(max, v));
    const y = pad + (1 - (clamped - min) / range) * innerH;
    return { x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(' ');

  const areaPath =
    `${linePath} L${points[points.length - 1].x.toFixed(2)},${(height - pad).toFixed(2)}` +
    ` L${points[0].x.toFixed(2)},${(height - pad).toFixed(2)} Z`;

  const last = points[points.length - 1];

  const baselineY =
    baseline !== undefined
      ? pad + (1 - (Math.max(min, Math.min(max, baseline)) - min) / range) * innerH
      : null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn(colorClass, className)}
      role="img"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity={0.22} />
          <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
        </linearGradient>
      </defs>
      {baselineY !== null && (
        <line
          x1={pad}
          y1={baselineY}
          x2={width - pad}
          y2={baselineY}
          stroke="currentColor"
          strokeWidth={1}
          strokeDasharray="3 3"
          className="text-muted-foreground/40"
        />
      )}
      <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
      <path
        d={linePath}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last.x} cy={last.y} r={strokeWidth * 1.4} fill="currentColor" />
    </svg>
  );
}
